import { onError } from '@actions/app.action';
import {
  coverGeniusRequestQuote,
  coverGeniusRequestQuoteSuccess,
  coverGeniusSelectPolicy,
  coverGeniusSelectPolicySuccess,
} from '@actions/cover-genius.action';
import { inject, Injectable } from '@angular/core';
import { CabinProductClass } from '@constants/cabin.constants';
import { BaseError, ErrorType } from '@models/app.models';
import { GrowthBookFeatureKeys } from '@models/growthbook.models';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { CoverGeniusService } from '@services/api-clients/cover-genius.service';
import { GrowthBookService } from '@services/growthbook/growthbook.service';
import { catchError, mergeMap, Observable, of, switchMap } from 'rxjs';

@Injectable()
export class CoverGeniusEffect {
  private readonly gbs = inject(GrowthBookService);
  
  private readonly gbf = GrowthBookFeatureKeys;

  constructor(
    private actions$: Actions,
    private coverGeniusService: CoverGeniusService,
  ) {}

  requestQuote$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(coverGeniusRequestQuote),
      mergeMap((action) => {
        return this.coverGeniusService.createQuote(action.bookingFlow).pipe(
          switchMap((response) =>
            of(coverGeniusRequestQuoteSuccess({ payload: response.data })),
          ),
          catchError(() =>
            of(
              onError({
                error: new BaseError(ErrorType.CoverGeniusQuoteError),
              }),
            ),
          ),
        );
      }),
    );
  });

  selectPolicy$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(coverGeniusSelectPolicy),
      mergeMap((action) => {
        return this.coverGeniusService.select(action.payload).pipe(
          switchMap((response) => {            
            let bookingCopy = {...response.data};
            if (
              this.gbs.growthBook.isOn(this.gbf.FEAT_US929_CB10_INCLUDED_WITH_ECONOMY_LIGHT) && 
              this.gbs.growthBook.getFeatureValue(this.gbf.FEAT_US929_CB10_INCLUDED_WITH_ECONOMY_LIGHT, '') === 'b') {
                bookingCopy = {
                  ...response.data,
                  bags: response.data.bags?.map(b => ({
                    ...b,
                    passengerBags: b.passengerBags.map(pb => ({
                      ...pb,
                      bags: pb.bags.map(bag => ({
                        ...bag,
                        inBundleCount: response.data.journeys.find(j => j.journeyKey === b.journeyKey)?.productClass === CabinProductClass.ECONOMY_LIGHT ? bag.inBundleCount + 1 : bag.inBundleCount
                      }))
                    }))
                  }))
                }
            }

            return of(coverGeniusSelectPolicySuccess({ payload: bookingCopy }))
          }),
          catchError(() =>
            of(
              onError({
                error: new BaseError(ErrorType.CoverGeniusSelectError),
              }),
            ),
          ),
        );
      }),
    );
  });
}
